import { memo, ReactElement, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';

import { Skeleton } from '@shared/ui/skeleton';

import styles from './daily-news.module.css';
import { useFeed, Item, useRevolveAd } from '../stories/top-stories.hooks';
import { useViewportFillingCount } from '../use-viewport-filling-count';
import { sanitizeHtml } from '@/lib/article-renderer/core/sanitize-html';
import { T, useT } from '@shared/i18n';

const FEED_URL = 'https://www.pw.org/daily_news/feed';

const DATE_FORMAT = 'MMMM dd, yyyy, hh:mm a';

export const DailyNews = () => {
	const t = useT();
	const navigate = useNavigate();

	const { feed, isLoading } = useFeed(FEED_URL);
	// `replace: true` is load-bearing, not a style preference: this effect runs
	// on every mount of this section, including landing back here via the
	// browser's own back button. Without it each of those mounts *pushed* a
	// fresh history entry rather than updating the current one, so pressing
	// back walked through the synthetic entries this effect kept adding
	// instead of the reader's actual navigation. Matching morningstar's
	// Phase 0.12 fix, which had one such effect; poets has four.
	useEffect(() => {
		navigate({
			to: '/',
			search: {
				section: 'daily-news',
			},
			replace: true,
		});
	}, [navigate]);
	useEffect(() => {
		if (isLoading) {
			return;
		}
		if (!feed) {
			return;
		}
		setTimeout(() => {
			const links = document.querySelectorAll('#feed a');
			if (!links) {
				return;
			}

			links.forEach((e) => {
				if (e instanceof HTMLElement) {
					e.setAttribute('target', '_blank');
				}
			});
		}, 1000);
	}, [isLoading, feed]);

	// Same fix as top-stories.tsx: the rows and their interleaved ad banners
	// were rebuilt on every render even though `feed` only changes when the
	// RSS query resolves. These rows render publisher HTML through
	// dangerouslySetInnerHTML, so re-creating them is not free.
	const feedItems = useMemo(
		() =>
			feed
				.map((item: Item, i: number) => (
					<FeedItem key={i} {...item} index={i} />
				))
				.reduce(reduceBanner, [] as Array<ReactElement>),
		[feed],
	);

	return (
		<section className={styles['daily-news']}>
			<h1>{t('template.poets.dailyNews')}</h1>
			<h2>
				<T k="template.poets.dailyNewsIntro" components={{ br: () => <br /> }} />
			</h2>
			<div className={styles['feed-container']}>
				{isLoading ? (
					<FeedSkeleton />
				) : (
					<div id="feed" className={styles['feed']}>
						{feedItems}
					</div>
				)}
				<footer>
<T
						k="template.poets.discoverMore"
						components={{
							link: (chunks) => (
								<a href="https://www.pw.org" rel="noreferrer" target="_blank">
									{chunks}
								</a>
							),
						}}
					/>
				</footer>
			</div>
		</section>
	);
};

/**
 * Loading state for Daily News -- a skeleton shaped like the real rows
 * (a short uppercase date/time line, then a run of body-text lines) rather
 * than the centered spinner this replaced. Same reasoning as top-stories.tsx's
 * own `FeedSkeleton` (morningstar's Phase 0.21): a spinner gives the section
 * no height, so the real feed lands and shoves the page down the moment it
 * arrives; reserving the real row shapes up front makes it a fill, not a
 * jump.
 *
 * Real rows here have no thumbnail (unlike top-stories' `.feed-item`) --
 * just `.title` (one line, the item's date) and `.content` (the RSS
 * description, rendered as one or more paragraphs of unpredictable length).
 * Three lines of decreasing width approximate a short paragraph without
 * claiming to reserve the exact height a real, variable-length description
 * will need -- the real rows already don't share a fixed height with each
 * other either.
 *
 * Row count comes from `useViewportFillingCount` (shared with top-stories'
 * own `FeedSkeleton`), not a fixed number -- a fixed count is right for at
 * most one breakpoint and leaves the rest of the section blank on any
 * taller viewport. `.feed` here is a single column at every breakpoint
 * (unlike top-stories' 1/2/3-column grid), so the hook's column read is
 * always 1, but the height math is the same fill-to-the-bottom logic either
 * way -- desktop, tablet, and mobile all just measure a different row
 * height and viewport, not a different code path.
 */
function FeedSkeleton() {
	const { containerRef, count } = useViewportFillingCount();
	return (
		<div className={styles['feed']} aria-busy="true" ref={containerRef}>
			{Array.from({ length: count }).map((_, i) => (
				<div key={i} className={styles['feed-item-container']}>
					<div className={styles['feed-item']}>
						<div className={styles['title']}>
							<Skeleton className="h-3 w-40 bg-gray-300" />
						</div>
						<div className={styles['content']}>
							<Skeleton className="h-4 w-full mb-2 bg-gray-300" />
							<Skeleton className="h-4 w-full mb-2 bg-gray-300" />
							<Skeleton className="h-4 w-2/3 bg-gray-300" />
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

function reduceBanner(
	acc: Array<ReactElement>,
	item: ReactElement,
	i: number,
): Array<ReactElement> {
	acc.push(item);
	if (i === 2) {
		acc.push(
			<Banner
				key={`0-${i}`}
				id="fbf717dc1969dc79e517b3994cd0df44"
				zone={66}
				ad="ADVERTISEMENT"
			/>,
		);
	}

	if (i === 5) {
		acc.push(
			<Banner
				key={`0-${i}`}
				id="fbf717dc1969dc79e517b3994cd0df44"
				zone={63}
				ad="ADVERTISEMENT"
			/>,
		);
	}

	return acc;
}

function Banner({ id, zone, ad }: { id: string; zone: number; ad?: string }) {
	const ref = useRef<HTMLDivElement>(null);
	useRevolveAd(ref);
	return (
		<div ref={ref} className={styles['banner']}>
			<div>
				<div className={styles['ins-wrapper']}>
					<ins data-revive-zoneid={zone} data-revive-id={id}></ins>
				</div>
				{ref && ad ? <div className={styles['ad']}>{ad}</div> : null}
			</div>
		</div>
	);
}

const FeedItem = memo(function FeedItem({
	description,
	index,
	pubDate,
}: Item) {
	return (
		// eslint-disable-line jsx-a11y/no-static-element-interactions
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
		<div className={styles['feed-item-container']} data-index={index}>
			<div className={styles['feed-item']}>
				<div className={styles['title']}>
					<h3>
						{pubDate
							? DateTime.fromJSDate(pubDate).toFormat(DATE_FORMAT)
							: null}
					</h3>
				</div>
				<div className={styles['content']}>
					<div
						dangerouslySetInnerHTML={{ __html: sanitizeHtml(description ?? '') }}
					></div>
				</div>
			</div>
		</div>
	);
});
